import { LITERAL_TYPES, NUMBER_SUFFIX_SPACER, isLiteral } from './literalUtility'
import { formatTimestampToInputValue, isValidSoqlDate, TIMEZONE } from './dateUtility'
import Datetime from 'react-datetime'
import Input from '../Input/Input'
import Literal from './Literal'
import PropTypes from 'prop-types'
import React from 'react'
import ReactDOM from 'react-dom'
import { ReactDateTimeStyle } from './DatePickerStyles'
import Tabs from '../../Tabs/Tabs'
import TetherComponent from 'react-tether'
import moment from 'moment-timezone'
import Icon from '../../Icon/Icon'
import { checkIfOnlyDigits } from '../../../utility'
import { dateParser } from './looseDateParser'

const INVALID_DATE_ERROR_MESSAGE = 'Invalid date!'

export default class DatePickerComponent extends React.Component {
  static propTypes = {
    value: PropTypes.string,
    onChange: PropTypes.func,
    isTime: PropTypes.bool,
    isEmptyAllowed: PropTypes.bool,
    className: PropTypes.string,
    isWhereClause: PropTypes.bool,
    label: PropTypes.string,
    inputLeftItem: PropTypes.node,
    inputRightItem: PropTypes.node
  }

  static defaultProps = {
    isTime: false,
    isEmptyAllowed: true,
    className: ''
  }

  constructor (props) {
    super(props)
    this.state = {
      isLiteral: isLiteral(props.value),
      isLiteralTab: false,
      isOpen: props.autoFocus || false,
      isEditingDate: false,
      editValue: '',
      isLookupOpened: false,
      isNearBottom: false,
      position: {
        top: 0,
        height: 0
      },
      errorMessage: ''
    }
    this.inputRef = React.createRef()
  }

//This handler exists outside the React event system and in a sometimes portaled component.
// The class-based selectors are a compromise from this and are the most stable way to address the appropriate nodes without rewriting the component.
  handleClick = (e) => {
    const { isOpen } = this.state
    const tetherElement = e.target.closest(".tether-element");
    const dropdown = e.target.closest(".react-select__option");
    const tetherTarget = e.target.closest(".tether-target");
    const popupClick = tetherElement || dropdown;
    if (!popupClick && isOpen && !tetherTarget) {
      this.handleClose();
    }
  }

  haltEvent = (e) => {
    if (this.state?.isOpen) {
      e.preventDefault()
      e.stopPropagation()
    }
  }

  componentDidMount = () => {
    document.addEventListener('click', this.handleClick, false)
    this.setPosition()
  }

  componentWillUnmount = () => document.removeEventListener('click', this.handleClick, false)

  setPosition = () => {
    if (this.trigger) {
      this.setState({ position: ReactDOM.findDOMNode(this.trigger).getBoundingClientRect() })
    }
  }

  onClickDate = () => this.setState({ isLiteral: false, isOpen: false })

  onClickLiteral = () => this.setState({ isLiteral: true })

  onInputChange = (e) => {
    const { value } = e.target
    this.setState({
      isEditingDate: true,
      editValue: value,
      isOpen: false,
      ...value && { errorMessage: '' }
    })
  }

  onBlur = () => {
    if (this.state.isOpen) return
    const { editValue } = this.state
    this.setState({
      isEditingDate: false,
      isOpen: false
    })
    const momentValue = moment(editValue)
    const isValidDateInput = momentValue.isValid()
    if (!isValidDateInput) return this.props.onChange && this.props.onChange(this.props.value)
    this.setState({
      isNearBottom: false
    })
    if (this.props.isTime) {
      this.props.onChange(momentValue)
    } else {
      this.props.onChange(moment(momentValue))
    }
  }

  onKeyDown = (e) => {
    if (e.keyCode === 13) {
      const { config: { inputDateFormat } = {} } = this.props
      const { value } = e.target
      if (!value) return
      const { type } = this.props
      const regExContainAmPm = /\b((1[0-2]|0?[1-9]):?([0-5][0-9])? ?([AaPp][Mm]))/g
      const containsAmPm = regExContainAmPm.test(value)
      const momentValue = dateParser(value.trim(), inputDateFormat)

      const isValidDateInput = momentValue.isValid()
      if (!isValidDateInput) {
        this.setState({
          errorMessage: INVALID_DATE_ERROR_MESSAGE
        })
        return this.props.onChange && this.props.onChange('')
      }

      if (type === 'DATETIME') {
        const isToday = this.isToday(momentValue.toDate())
        const today = new Date()
        const currentHour = today.getHours()

        if (!containsAmPm) {
          momentValue.set({
            hour: isToday ? currentHour + 1 : 9,
            minute: 0,
            second: 0,
            millisecond: 0
          })
        }
      }

      this.setState({
        isOpen: false
      })

      this.onDateChange(momentValue)
    }

    // on tab key pressed
    if (e.keyCode === 9) {
      if (this.state.isOpen && this.props.closeOnBlur) {
        this.setState({
          isOpen: false
        })
      }
    }
  }

  onInputClick = () => {
    const { bottom } = ReactDOM.findDOMNode(this.trigger).getBoundingClientRect() || {}
    const { innerHeight } = window || {}
    this.setState({
      isOpen: !this.state.isOpen,
      isNearBottom: innerHeight - bottom < 350,
      errorMessage: ''
    })
  }

  onDateChange = (v) => {
    const { type, onValueChange, onChange, isTime } = this.props
    if (!v.isValid()) {
      return
    }
    let timezoned = moment.tz(v, TIMEZONE)
    if (isTime) {
      onChange(v)
      onValueChange?.(v)
    } else {
      timezoned = moment.utc(timezoned).format()
      const val = moment(v).format()
      onChange(val)
      onValueChange?.(val)
    }
    this.setState({
      isOpen: type !== 'DATE',
      isEditingDate: false,
      editValue: timezoned?.valueOf()
    })
  }

  onLiteralOpen = () => this.setState({ isLookupOpened: true })

  onLiteralClose = () => this.setState({ isLookupOpened: false })

  handleTabChange = (tab) => this.setState({ isLiteralTab: tab === 2 })

  handleClose = () => this.setState({ isOpen: false })

  clearValue = () => {
    this.setState({
      isOpen: false
    })
    this.props.onChange(null)
  }

  isDateValid = (current) => {
    let { minDate, maxDate, maxToday, minToday } = this.props
    let isDateOk = true

    if (minToday) {
      minDate = moment().subtract(1, 'day')
    } else if (minDate) {
      minDate = moment(minDate).subtract(1, 'day')
    }

    if (maxToday) {
      maxDate = moment()
    } else if (maxDate) {
      maxDate = moment(maxDate).add(1, 'day')
    }

    if (minDate) {
      isDateOk = current.isAfter(minDate)
    }

    if (maxDate && isDateOk) {
      isDateOk = current.isBefore(maxDate)
    }
    return isDateOk
  }

  isToday = (date) => {
    const today = new Date()
    return date.getDate() === today.getDate() &&
      date.getMonth() === today.getMonth() &&
      date.getFullYear() === today.getFullYear()
  }

  render () {
    const {
      isTime,
      isEmptyAllowed,
      className,
      isWhereClause,
      label,
      style,
      inputLeftItem,
      inputRightItem,
      dateFormat,
      placeholder,
      required,
      helpText,
      helpTextTools,
      type,
      icon,
      config: {
        useLiterals = false
      } = {}
    } = this.props
    let {
      value
    } = this.props
    if (typeof value === 'string' && checkIfOnlyDigits(value)) value = parseInt(value, 10)
    const { isOpen, isLiteral, isEditingDate, editValue } = this.state
    const momentValue = moment(value)
    const validDateInput = momentValue.isValid()
    let inputValue = value
    if (validDateInput) {
      inputValue = !isLiteral ? formatTimestampToInputValue(value, isTime, dateFormat, type) : (typeof value === 'number' ? formatTimestampToInputValue(value, isTime, dateFormat, type) : value)
    }
    const isDateFormat = isValidSoqlDate(inputValue, isTime)
    const dateValue = value ? moment.tz(value, TIMEZONE) : null
    let isValid = false
    if (!value && isEmptyAllowed) {
      isValid = true
    } else if (value && isDateFormat) {
      isValid = true
    } else if (value && typeof value === 'number') {
      isValid = true
    } else if (value && typeof value === 'string') {
      isValid = validDateInput
      const splittedValue = value.split(NUMBER_SUFFIX_SPACER)
      const foundedLiteral = LITERAL_TYPES.find(el => el.includes(splittedValue[0]))
      if (foundedLiteral) {
        if (value.includes(NUMBER_SUFFIX_SPACER)) {
          isValid = `${value.split(NUMBER_SUFFIX_SPACER)[0]}${NUMBER_SUFFIX_SPACER}` === foundedLiteral
        } else {
          isValid = value === foundedLiteral
        }
        inputValue = value
      }
    }
    const tetherOffset = type === 'DATETIME' ? '-400px 0' : '-360px 0'

    return (
      <div className='datePickerComponent w-100' ref={trigger => { this.trigger = trigger }} style={{ ...style }}>
        <TetherComponent
          style={{ zIndex: 999999 }}
          attachment='top left'
          targetAttachment='bottom left'
          targetOffset={this.state.isNearBottom ? tetherOffset : '10px 0'}
          renderTarget={ref => (<div className='w-100' ref={ref}>
            <Input
              value={isEditingDate ? editValue : inputValue || ''}
              onFocus={this.handleOpen}
              onBlur={this.onBlur}
              onChange={this.onInputChange}
              error={this.state.errorMessage || this.props.error || !isValid}
              onClick={this.onInputClick}
              className={className}
              label={label || ''}
              leftItem={inputLeftItem}
              rightItem={inputRightItem}
              placeholder={placeholder}
              onKeyDown={this.onKeyDown}
              required={required}
              helpText={helpText}
              helpTextTools={helpTextTools}
              icon={inputValue ? 'close' : icon}
              { ...inputValue && { onIconClick: this.clearValue }}
              autoFocus={this.props.autoFocus}
            />
          </div>
          )}
          renderElement={ref => isOpen && (
            <div ref={ref} onClick={this.haltEvent}>
              <div className='dropdown' ref={dropdown => { this.dropdown = dropdown }}>
                <div className='has_no_error' style={{ display: 'inline-table' }}>
                  {
                    (useLiterals || isWhereClause)
                      ? <Tabs
                        tabs={[
                          { id: 1, props: { label: 'Date' } },
                          { id: 2, props: { label: 'Literal' } }
                        ]}
                        handleTabChange={this.handleTabChange}
                        defaultSelectedTab={isLiteral ? 1 : 0}
                        horizontalAlign='left'
                      >
                      <DateInput
                        type={type}
                        isDateValid={this.isDateValid}
                        isTime={isTime}
                        dateValue={dateValue}
                        handleClose={this.handleClose}
                        onDateChange={this.onDateChange}
                      />
                      <Literal
                        {...this.props}
                        isInstant={useLiterals}
                        onOpen={this.onLiteralOpen}
                        onClose={this.onLiteralClose}
                        handleClose={this.handleClose}
                      />
                    </Tabs>
                      : <DateInput
                        type={type}
                        isDateValid={this.isDateValid}
                        isTime={isTime}
                        dateValue={dateValue}
                        handleClose={this.handleClose}
                        onDateChange={this.onDateChange}
                      />
                  }
                </div>
              </div>
            </div>
          )}
        />
        <style jsx>{`
          .dropdown {
            z-index: 1000001;
            padding: 0.5rem;
            background: var(--pageBackground, #fff);
            border: 1px solid var(--paleGrey, #ececee);
            border-radius: var(--borderRadius, 4px);
            box-shadow: 0 2px 6px 0 rgba(0, 0, 0, 0.1);

            :global(.rdtPicker) {
              position: relative;
              border: none;
              box-shadow: none;
            }

            :global(.rdtActive) {
              border-radius: 50%;
            }

            :global(.rdtActive) {
              width: 100%;
            }

            :global(.rdtPicker td) {
              width: 35px;
              height: 35px;
            }
            :global(.rdtTimeToggle:hover) {
              background: transparent;
              background-image: linear-gradient(left, transparent 20%, #eeeeee 20%, #eeeeee 80%, transparent 80%, transparent 100%);
              background-image: -webkit-linear-gradient(left, transparent 20%, #eeeeee 20%, #eeeeee 80%, transparent 80%, transparent 100%)
            }
            :global(.rdtPicker td),
            :global(.rdtPicker th) {
              padding: 0;
            }

            .dropdown :global(.Tabs) {
              margin-bottom: 0.5rem;
            }
            
            :global(.tab-content) {
              margin-bottom: 0.5rem;
            }
            
            :global(.close) {
              position: absolute;
              right: 10px;
              bottom: 5px;
              z-index: 99999;
            }
          }
          .has_no_error select, .has_no_error input {
            border-color: #d8dde6 !important;
            background-color: #fff !important;
            box-shadow: none !important;
          }
          .portal_modal {
            position: fixed;
            top: 3rem;
            z-index: 10;
          }
          `}</style>
        <style jsx>{ReactDateTimeStyle}</style>
      </div>
    )
  }
}

const DateInput = (props) => (
  <div className={'position-relative'}>
    <Datetime
      dateFormat={!(props.type === 'TIME')}
      isValidDate={props.isDateValid}
      renderInput={() => { }}
      timeFormat={props.isTime}
      open
      // utc={!props.isTime}
      onChange={props.onDateChange}
      defaultValue={props.dateValue || false}
    />
    {
      props.type === 'DATETIME' &&
      <div className={'close'}>
        <Icon icon={'close'} iconSize={'large'} onClick={props.handleClose} pointer />
      </div>
    }
  </div>
)
